export const NOTIFICATION_TYPES = [
  'adoption_update',
  'new_intelligence',
  'community_contribution',
  'accountability_record',
  'social_activity',
  'system',
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];
export type UserNotificationType = Exclude<NotificationType, 'system'>;

export type NotificationPreferences = Partial<Record<UserNotificationType, boolean>>;

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  adoption_update: true,
  new_intelligence: true,
  accountability_record: true,
  social_activity: false,
  community_contribution: false,
};

export const NOTIFICATION_PREFERENCE_FIELDS: Array<{
  key: UserNotificationType;
  label: string;
  hint: string;
}> = [
  {
    key: 'adoption_update',
    label: 'Score Movement',
    hint: 'Alert when a watched official’s spine score moves.',
  },
  {
    key: 'new_intelligence',
    label: 'Fresh Intelligence',
    hint: 'Alert when a new intelligence briefing lands on the dossier.',
  },
  {
    key: 'accountability_record',
    label: 'Public Record Update',
    hint: 'Alert when a new accountability record or official document is added.',
  },
  {
    key: 'social_activity',
    label: 'Social Activity',
    hint: 'Alert when meaningful public social activity is captured.',
  },
  {
    key: 'community_contribution',
    label: 'Community Contribution',
    hint: 'Alert when the community adds a reviewed contribution to the dossier.',
  },
];

export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  adoption_update: 'Score Movement',
  new_intelligence: 'Fresh Intelligence',
  community_contribution: 'Community Contribution',
  accountability_record: 'Public Record Update',
  social_activity: 'Social Activity',
  system: 'System',
};

export const NOTIFICATION_TYPE_TITLES: Record<NotificationType, string> = {
  adoption_update: 'Score Movement',
  new_intelligence: 'Fresh Intelligence',
  community_contribution: 'Community Contribution',
  accountability_record: 'Public Record Update',
  social_activity: 'Social Activity',
  system: 'System Notice',
};

export function normalizeNotificationPreferences(value: unknown): NotificationPreferences {
  if (!value || typeof value !== 'object') {
    return { ...DEFAULT_NOTIFICATION_PREFERENCES };
  }

  const input = value as Record<string, unknown>;
  const normalized: NotificationPreferences = {
    ...DEFAULT_NOTIFICATION_PREFERENCES,
  };

  if (typeof input.scoreChange === 'boolean') normalized.adoption_update = input.scoreChange;
  if (typeof input.newIntel === 'boolean') normalized.new_intelligence = input.newIntel;
  if (typeof input.publicRecord === 'boolean') normalized.accountability_record = input.publicRecord;
  if (typeof input.socialActivity === 'boolean') normalized.social_activity = input.socialActivity;

  if (typeof input.adoption_update === 'boolean') normalized.adoption_update = input.adoption_update;
  if (typeof input.new_intelligence === 'boolean') normalized.new_intelligence = input.new_intelligence;
  if (typeof input.accountability_record === 'boolean') normalized.accountability_record = input.accountability_record;
  if (typeof input.social_activity === 'boolean') normalized.social_activity = input.social_activity;
  if (typeof input.community_contribution === 'boolean') normalized.community_contribution = input.community_contribution;

  return normalized;
}

export function isNotificationEnabled(
  type: NotificationType,
  preferences: NotificationPreferences | null | undefined,
): boolean {
  if (type === 'system') {
    return true;
  }

  const normalized = normalizeNotificationPreferences(preferences ?? null);
  return normalized[type] !== false;
}
